import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { trackConnectorConnectFailed } from '@/lib/analytics/connectionTracking'
import {
  type GithubAuthState,
  GITHUB_AUTH_STATE_STORAGE_KEY,
  githubRedirectUri,
  relayGithubCallbackIfNeeded,
} from '@/lib/auth/github'
import { safeParseRnd } from '@/lib/auth/oauth-state'

// GitHub App install landing. GitHub redirects here after an install with
// `?installation_id=…&code=…&setup_action=install&state=…`. We validate state (CSRF) and hand
// both the installation id and the user-authorization code to the backend, which proves the
// user can see that installation before storing the connection.
//
// The `code` requires "Request user authorization (OAuth) during installation" on the App.
// Without it GitHub omits the code and we cannot verify ownership, so we stop here rather
// than sending an unverifiable installation id to the backend.
export function AuthGithubPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: connectGithub } = trpc.brandConnection.connectGithub.useMutation()

  const isMutationRunning = useRef(false)

  const installationId = searchParams.get('installation_id')
  const code = searchParams.get('code')
  const state = searchParams.get('state')

  useEffect(() => {
    // GitHub always lands on the App's first registered callback URL, which is production, so
    // a connect started from dev or preview arrives on the wrong host. Send it home before
    // anything else — the state this page needs lives in the starting origin's localStorage.
    if (relayGithubCallbackIfNeeded(state)) return

    if (isMutationRunning.current) return
    isMutationRunning.current = true

    const storedState = JSON.parse(
      localStorage.getItem(GITHUB_AUTH_STATE_STORAGE_KEY) || 'null',
    ) as GithubAuthState | null
    const referrerPath = storedState?.referrerPath ?? '/account'

    // State is required, not just checked-when-present. Treating it as optional left the
    // callback open to CSRF: a crafted /auth/github?installation_id=…&code=… link with no
    // state would connect the attacker's own installation to whichever brand the victim is
    // signed into. Installing straight from GitHub's own UI arrives without state and so
    // lands here — that's deliberate, the connection has to start from CASSI.
    if (!state || !storedState || storedState.rnd !== safeParseRnd(state)) {
      trackConnectorConnectFailed('github', 'state_mismatch')
      toast.error('GitHub connection failed', {
        description: 'Start the connection from CASSI settings and try again.',
      })
      localStorage.removeItem(GITHUB_AUTH_STATE_STORAGE_KEY)
      isMutationRunning.current = false
      navigate(referrerPath)
      return
    }

    // No installation id means GitHub did not finish an install (an org install that is
    // waiting on owner approval lands here too). Without this the spinner spins forever.
    if (!code || !installationId) {
      trackConnectorConnectFailed('github', 'missing_code')
      toast.error('GitHub connection failed', {
        description: 'GitHub did not confirm your account. Try connecting again.',
      })
      localStorage.removeItem(GITHUB_AUTH_STATE_STORAGE_KEY)
      isMutationRunning.current = false
      navigate(referrerPath)
      return
    }

    connectGithub({ installationId, code, redirectUri: githubRedirectUri() })
      .then((res) => {
        toast.success('GitHub connected', {
          description: res.accountLogin ? `Connected to ${res.accountLogin}` : undefined,
        })
        navigate(referrerPath)
      })
      .catch((error: unknown) => {
        trackConnectorConnectFailed('github', 'authorize_failed', error)
        toast.error('Failed to connect GitHub', {
          description: error instanceof Error ? error.message : undefined,
        })
        navigate(referrerPath)
      })
      .finally(() => {
        localStorage.removeItem(GITHUB_AUTH_STATE_STORAGE_KEY)
        isMutationRunning.current = false
      })
  }, [installationId, code, state, connectGithub, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting GitHub...</p>
      </div>
    </div>
  )
}
